import { useRef, useMemo } from "react";
import { CardHeader, Card, CardContent, CardActions, Avatar, Button, styled, alpha } from "@mui/material";
import { useState } from "react";
import moment from "moment-timezone";
import classNames from "classnames";

export interface IEventCommentProps {
  content: string;
  timeStamp?: number;
  user?: string;
  index?: number;
  dateFormat?: string;
  avatar?: any;
  containerHeight?: number;
}

const OCEventComment = styled(Card)(({ theme }) => ({
  width: 380,
  margin: 5,
  boxShadow: "unset",
  backgroundColor: theme.custom.comments.card.contentBackGround,
  "& .expand_button": {
    height: 28,
    marginTop: -20,
    justifyContent: "flex-end",
    padding: 0,
    "& .MuiButton-text": {
      padding: "2px 2px",
    },
  },
  "& .card_header": {
    padding: 5,
    "& .MuiCardHeader-content": {
      "& .MuiCardHeader-title": {
        fontSize: 12,
        fontWeight: 300,
        color: alpha(theme.custom.comments.card.contrastText, 0.75),
      },
      "& .MuiCardHeader-subheader": {
        fontSize: 12,
        fontWeight: 300,
        color: alpha(theme.custom.comments.card.contrastText, 0.75),
      },
    },
    "& .avatar": {
      backgroundColor: theme.custom.comments.card.avatar,
    },
  },
  "& .card_content": {
    ...theme.typography.body2,
    fontWeight: 300,
    padding: 5,
    position: "relative",
    maxHeight: 90,
    height: "auto",
    overflow: "hidden",
    whiteSpace: "pre",
    transition: theme.transitions.create("max-height", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    "&.showFade:after": {
      content: '""',
      position: "absolute",
      display: "block",
      width: "100%",
      height: 40,
      bottom: 0,
      backgroundImage: `linear-gradient(
          ${alpha(theme.custom.comments.card.contentBackGround, 0)},
          ${theme.custom.comments.card.contentBackGround})`,
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    "&.expanded": {
      overflowY: "auto",
      maxHeight: 1500,
      height: "auto",
      transition: theme.transitions.create("max-height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    "&.expanded.showFade:after": {
      height: 0,
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },
}));

/**
 * A componet that returns a single card - used to display flight comments
 * @param props - content: string, to displayed in card content
                timeStamp: Date, displayed in the card header
                user: string, the user identifier that you pass in 
 * @param props - OptionalProsp: dateFormat: string there is a default format or you can set your own using moment format
 *              avatar: any you can pass in an avatar to be displayed
 *              containerHeight: number default 100 or you can pass in your own
 * @returns a card that displays a single comment 
 */

export const EventComment = (props: IEventCommentProps): JSX.Element => {
  const { content, timeStamp, user, index, avatar, containerHeight } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpandClick] = useState<boolean>(index === 0);

  const dateFormat: string = props.dateFormat ? props.dateFormat : "ddd, D MMM YYYY HH:mm";

  // Determines whether to display a more button when scroll height of the content requires it.
  // const scrollHeight = containerRef?.current?.scrollHeight ?? 0;
  // const displayMoreButton = scrollHeight < (containerHeight ?? 100);

  const renderReadMoreButton = useMemo((): JSX.Element => {
    const buttonLabel: string = expanded ? "less" : "more";
    return (
      <Button
        id={"more_less_btn"}
        onClick={() => {
          setExpandClick(!expanded);
        }}
        color="primary"
        autoFocus={true}
      >
        {buttonLabel}
      </Button>
    );
  }, [expanded]);

  const renderAvatar = useMemo((): JSX.Element => {
    return avatar ? (
      <Avatar aria-label="index" className="avatar">
        {avatar}
      </Avatar>
    ) : null;
  }, [avatar]);

  ////////////
  // RETURN //
  ////////////

  return (
    <OCEventComment className={"event_comment"} style={{ height: "fit-content" }}>
      {user && (
        <CardHeader
          className={"card_header"}
          avatar={renderAvatar}
          title={user}
          subheader={!!timeStamp && moment.utc(timeStamp as moment.MomentInput).format(dateFormat)}
        />
      )}

      <CardContent ref={containerRef} className={classNames(expanded && "expanded", "showFade", "card_content")}>
        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", marginLeft: 10 }}>{content}</div>
      </CardContent>
      <CardActions className="expand_button">{renderReadMoreButton}</CardActions>
    </OCEventComment>
  );
};
